import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import config from '../config.json';

const toSlug = (children) => {
  const text = Array.isArray(children)
    ? children
        .map((c) => (typeof c === 'string' ? c : c?.props?.children ?? ''))
        .join('')
    : String(children ?? '');
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

function SupportPage() {
  const theme = useTheme();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(config.urls.userManual)
      .then((res) => res.text())
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch(() => {
        setMarkdown('# Error\n\nUnable to load the user manual.');
        setLoading(false);
      });
  }, []);

  const markdownComponents = {
    h1: ({ children }) => (
      <Typography
        id={toSlug(children)}
        variant="h2"
        sx={{
          mt: 6,
          mb: 3,
          fontWeight: 800,
          background: theme.custom.gradientPrimary,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          scrollMarginTop: '100px',
        }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        id={toSlug(children)}
        variant="h3"
        sx={{
          mt: 6,
          mb: 2,
          fontWeight: 700,
          color: 'text.primary',
          borderBottom: `2px solid ${theme.custom.subtleBorder}`,
          pb: 1,
          scrollMarginTop: '100px',
        }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        id={toSlug(children)}
        variant="h4"
        sx={{ mt: 4, mb: 1.5, fontWeight: 600, color: 'text.primary', scrollMarginTop: '100px' }}
      >
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography
        id={toSlug(children)}
        variant="h5"
        sx={{ mt: 3, mb: 1, fontWeight: 600, color: 'text.primary', scrollMarginTop: '100px' }}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.8 }}
      >
        {children}
      </Typography>
    ),
    a: ({ href, children }) => (
      <Link
        href={href}
        sx={{ color: 'primary.main', textDecoration: 'underline' }}
      >
        {children}
      </Link>
    ),
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{
          color: 'text.secondary',
          mb: 2,
          pl: 3,
          '& li': { mb: 0.5, lineHeight: 1.8 },
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box
        component="ol"
        sx={{
          color: 'text.secondary',
          mb: 2,
          pl: 3,
          '& li': { mb: 0.5, lineHeight: 1.8 },
        }}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        sx={{
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          pl: 3,
          py: 1,
          my: 2,
          backgroundColor: theme.custom.hoverBg,
          borderRadius: '0 8px 8px 0',
          '& p': { mb: 0 },
        }}
      >
        {children}
      </Box>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <Box
          component="code"
          sx={{
            backgroundColor: theme.custom.hoverBg,
            color: 'primary.main',
            px: 0.8,
            py: 0.3,
            borderRadius: 1,
            fontSize: '0.875em',
            fontFamily: 'monospace',
          }}
        >
          {children}
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            backgroundColor: theme.custom.sectionTint,
            border: `1px solid ${theme.custom.subtleBorder}`,
            borderRadius: 2,
            p: 2,
            mb: 2,
            overflow: 'auto',
            '& code': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              color: 'text.primary',
            },
          }}
        >
          <code>{children}</code>
        </Box>
      ),
    table: ({ children }) => (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${theme.custom.subtleBorder}`,
          borderRadius: 2,
        }}
      >
        <Table size="small">{children}</Table>
      </TableContainer>
    ),
    thead: ({ children }) => (
      <TableHead
        sx={{
          backgroundColor: theme.custom.sectionTint,
          '& th': { fontWeight: 700, color: 'text.primary' },
        }}
      >
        {children}
      </TableHead>
    ),
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    td: ({ children }) => (
      <TableCell sx={{ color: 'text.secondary' }}>{children}</TableCell>
    ),
    th: ({ children }) => (
      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
        {children}
      </TableCell>
    ),
    hr: () => (
      <Divider sx={{ my: 4, borderColor: theme.custom.subtleBorder }} />
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {children}
      </Box>
    ),
    img: ({ src, alt }) => (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          maxWidth: '100%',
          borderRadius: 2,
          my: 2,
          border: `1px solid ${theme.custom.subtleBorder}`,
        }}
      />
    ),
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 12 },
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="md">
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                '& > *:first-of-type': { mt: 0 },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {markdown}
              </ReactMarkdown>
            </Box>
          )}
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default SupportPage;
